<template>
    <div>
        <login-header />
        <div class="login">
            <div class="logo-icon">
                <div class="mr-re"></div>
                <div class="mr-bil"></div>
                <div class="mr-ly"></div>
            </div>
            <div class="tile stack-m">
                <div class="tile-title">
                    <h2>Log in to Rebilly</h2>
                </div>
                <div class="tile-content">
                    <template v-if="!isTwoFactorLogin">
                        <r-input
                            data-cy="email"
                            label="Email"
                            v-model="form.email"
                            :validate="$v.form.email"
                            class="stack-m"
                            left-icon="customers"
                            :auto-focus="true"
                            @key-submit="submit"
                        />
                        <r-input
                            data-cy="password"
                            label="Password"
                            v-model="form.password"
                            :validate="$v.form.password"
                            class="stack-l"
                            left-icon="lock"
                            :password="true"
                            @key-submit="submit"
                        />
                    </template>
                    <template v-else>
                        <r-input
                            label="Two-Factor Authentication Token"
                            v-model="form.oneTimePassword"
                            :validate="$v.form.oneTimePassword"
                            class="stack-m"
                            left-icon="lock"
                            @key-submit="submit"
                            name="tfa"
                        />
                        <r-button class="stack-m" :fluid="true" @click="cancelTwoFactor">Cancel Token Login</r-button>
                    </template>
                    <r-button
                        data-cy="login"
                        type="primary"
                        :loading="isLoading"
                        class="stack-m"
                        :fluid="true"
                        @click="submit">Log In</r-button>
                    <p class="text-center">
                        <router-link :to="{name: 'forgot-password'}" :disabled="isLoading">Forgot Password?</router-link>
                    </p>
                </div>
            </div>
            <v-error-messages
                :has-frontend-errors="$v.form.$error"
                :server-error="serverError"
                class="stack-m">
                <li class="list-item" v-if="!$v.form.email.required">Email is required</li>
                <li class="list-item" v-if="!$v.form.email.email">Email is invalid</li>
                <li class="list-item" v-if="$v.form.password.$dirty && !$v.form.password.required">Password is required</li>
                <li class="list-item" v-if="!$v.form.oneTimePassword.required">Two-Factor Authentication Token is required</li>
            </v-error-messages>
            <div class="tile tile-secondary">
                <div class="tile-content">
                    <p class="text-center">Need an account? <router-link :to="{name: 'signup'}">Sign Up</router-link></p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import LoginHeader from '@/modules/guest/login-header';
    import {
        vCheckbox, vErrorMessages,
    } from '@/components/form';
    import config from '@/common/config';
    import {email, required, requiredIf} from 'vuelidate/lib/validators';
    import moment from 'moment';

    export default {
        components: {
            LoginHeader, vCheckbox, vErrorMessages,
        },
        data() {
            return {
                form: {
                    email: null,
                    password: null,
                    oneTimePassword: null,
                },
                isLoading: false,
                isTwoFactorLogin: false,
                serverError: null,
            };
        },
        validations: {
            form: {
                email: {required, email},
                password: {required},
                oneTimePassword: {
                    /* eslint-disable func-names */
                    required: requiredIf(function () {
                        return this.isTwoFactorLogin;
                    }),
                },
            },
        },
        methods: {
            async submit() {
                this.$v.$reset();
                this.$v.$touch();
                if (!this.$v.form.$error) {
                    this.isLoading = true;
                    try {
                        const expiredTime = moment.utc()
                            .add(Number(config.VUE_APP_REMEMBER_DURATION_MS), 'ms').format();
                        const data = {
                            email: this.form.email,
                            password: this.form.password,
                            expiredTime,
                        };
                        if (this.isTwoFactorLogin) {
                            data.oneTimePassword = this.form.oneTimePassword;
                        }
                        const response = await this.$api(({account}) => account.signIn({data}));
                        const user = {
                            email: this.form.email,
                            impersonate: false,
                            ...response.getJSON().fields,
                        };
                        await this.$store.dispatch('app/login', user);
                        // set page to loading as we enter it
                        this.$startLoadingPage();
                    /**
                         * when user is authenticated successfully,
                         * redirect to dashboard page in permissions-sync plugin
                         * after permissions updated
                         */
                    } catch (error) {
                        const {status, response} = error;
                        // check if user has 2FA enabled
                        if (status === 401 && 'reb-2fa' in response.headers) {
                            this.isTwoFactorLogin = true;
                            this.serverError = null;
                        } else {
                            this.form.password = '';
                            this.$nextTick(() => {
                                this.$v.form.password.$reset();
                            });
                            this.serverError = error;
                        }
                        this.isLoading = false;
                    }
                }
            },
            cancelTwoFactor() {
                this.isTwoFactorLogin = false;
                this.form.oneTimePassword = '';
                this.form.email = '';
                this.form.password = '';
                this.$v.$reset();
            },
        },
    };
</script>

<docs>
    ```jsx
    <login/>
    ```
</docs>
